import { z } from 'zod';

/**
 * Politique de mot de passe par défaut (alignée sur US-AUTH-01) :
 * ≥ 8 chars, majuscule, minuscule, chiffre, caractère spécial.
 * Les tenants pourront configurer une politique plus stricte
 * via US-ADM-05 (`tenants.parametres.password_policy`).
 */
export const passwordPolicySchema = z
  .string()
  .min(8, 'Au moins 8 caractères')
  .max(255)
  .regex(/[A-Z]/, 'Au moins une majuscule')
  .regex(/[a-z]/, 'Au moins une minuscule')
  .regex(/[0-9]/, 'Au moins un chiffre')
  .regex(/[^A-Za-z0-9]/, 'Au moins un caractère spécial');

/** US-AUTH-05 — Bootstrap du super-admin plateforme (route `/my-secret-admin`) */
export const bootstrapAdminSchema = z.object({
  first_name: z.string().min(1).max(100),
  last_name: z.string().min(1).max(100),
  email: z.string().email('Email invalide').max(255),
  password: passwordPolicySchema,
});

export type BootstrapAdminInput = z.infer<typeof bootstrapAdminSchema>;

/** US-AUTH-02 — Schéma de connexion (login) */
export const loginSchema = z.object({
  email: z.string().email('Email invalide').max(255),
  password: z.string().min(1, 'Mot de passe requis').max(255),
});

export type LoginInput = z.infer<typeof loginSchema>;

/** US-AUTH-04 — Mise à jour du profil personnel. Email et rôle non modifiables. */
export const updateProfileSchema = z.object({
  first_name: z.string().min(1).max(100).optional(),
  last_name: z.string().min(1).max(100).optional(),
  /** Valeurs libres du Form Builder (US-ADM-08). Aucune validation forcée ici. */
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/** US-AUTH-04 — Changement de mot de passe (saisie mdp actuel + confirmation). */
export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1),
    new_password: passwordPolicySchema,
    new_password_confirm: z.string(),
  })
  .refine((d) => d.new_password === d.new_password_confirm, {
    path: ['new_password_confirm'],
    message: 'Les mots de passe ne correspondent pas',
  });

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/** US-AUTH-03 — Demande de réinitialisation */
export const forgotPasswordSchema = z.object({
  email: z.string().email('Email invalide').max(255),
});

export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/** US-AUTH-03 — Soumission du nouveau mot de passe via lien */
export const resetPasswordSchema = z
  .object({
    token: z.string().min(1),
    password: z
      .string()
      .min(8, 'Au moins 8 caractères')
      .regex(/[A-Z]/, 'Au moins une majuscule')
      .regex(/[a-z]/, 'Au moins une minuscule')
      .regex(/[0-9]/, 'Au moins un chiffre')
      .regex(/[^A-Za-z0-9]/, 'Au moins un caractère spécial'),
    passwordConfirm: z.string(),
  })
  .refine((d) => d.password === d.passwordConfirm, {
    path: ['passwordConfirm'],
    message: 'Les mots de passe ne correspondent pas',
  });

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
